import { normalizeOptimizerAvailability } from '../../Scheduling2025/scheduleOptimizer'

export const MAGIC_SCHEDULER_TEACHER_COLORS = [
    '#F97316',
    '#EF4444',
    '#A855F7',
    '#2563EB',
    '#E11D8A',
    '#0EA5E9',
    '#EAB308',
    '#7C3AED',
]

export const MAGIC_SCHEDULER_FAMILY_COLOR = '#16A34A'
export const MAGIC_SCHEDULER_FAMILY_BORDER_COLOR = '#14532D'

const normalize = (value) => String(value || '').trim()

export const getSuggestionObject = (suggestion = {}) => (
    suggestion?.eventOrTeacherObject || suggestion || {}
)

export const getSuggestionTeacherId = (suggestion = {}) => {
    const value = getSuggestionObject(suggestion)
    return normalize(
        value.teacherId
        || value.teacherID
        || value.TeacherID
        || value.TeacherId
        || value.id
    )
}

export const getSuggestionTeacherName = (suggestion = {}) => {
    const value = getSuggestionObject(suggestion)
    return normalize(value.teacherName || value.Name || value.name) || 'Teacher'
}

export const getSuggestionTeacherKey = (suggestion = {}) => {
    const teacherId = getSuggestionTeacherId(suggestion)
    if (teacherId) return `id:${teacherId}`
    return `name:${getSuggestionTeacherName(suggestion).toLowerCase()}`
}

export const getSuggestionKey = (suggestion = {}, index = 0) => {
    const value = getSuggestionObject(suggestion)
    const directKey = suggestion.suggestionId
        || suggestion.id
        || value.calendarEventId
        || value.eventId
        || value._id
    if (directKey) return String(directKey)

    const compositeKey = [
        getSuggestionTeacherKey(suggestion),
        value.type,
        value.familyId,
        value.day,
        value.time,
        suggestion.duration,
    ].filter(Boolean).join(':')

    return compositeKey || `suggestion-${index}`
}

export const createTeacherColorMap = (suggestions = []) => {
    const colorMap = {}
    ;(Array.isArray(suggestions) ? suggestions : []).forEach((suggestion) => {
        const teacherKey = getSuggestionTeacherKey(suggestion)
        if (!teacherKey || colorMap[teacherKey]) return
        const colorIndex = Object.keys(colorMap).length % MAGIC_SCHEDULER_TEACHER_COLORS.length
        colorMap[teacherKey] = MAGIC_SCHEDULER_TEACHER_COLORS[colorIndex]
    })
    return colorMap
}

export const getUniqueSuggestedTeachers = (suggestions = []) => {
    const colorMap = createTeacherColorMap(suggestions)
    const seen = new Set()

    return (Array.isArray(suggestions) ? suggestions : [])
        .map((suggestion) => {
            const teacherKey = getSuggestionTeacherKey(suggestion)
            if (seen.has(teacherKey)) return null
            seen.add(teacherKey)
            return {
                key: teacherKey,
                teacherId: getSuggestionTeacherId(suggestion),
                teacherName: getSuggestionTeacherName(suggestion),
                color: colorMap[teacherKey],
                suggestion,
            }
        })
        .filter(Boolean)
}

export const formatMapAddress = (address) => {
    if (typeof address === 'string') return normalize(address)
    if (!address || typeof address !== 'object') return ''

    const lineOne = normalize(
        address.addressLine1
        || address.StreetAddress
        || address.streetAddress
        || address.street
    )
    const lineTwo = normalize(address.addressLine2)
    const city = normalize(address.city || address.City)
    const state = normalize(address.state || address.State)
    const zipcode = normalize(address.zipcode || address.zipCode || address.Zipcode)
    const cityState = [city, state].filter(Boolean).join(', ')

    return [lineOne, lineTwo, cityState, zipcode].filter(Boolean).join(', ')
}

export const getLocationFromRecord = (record = {}) => {
    const sources = [
        record,
        record.location,
        record.coordinates,
        record.familyLocation,
        record.familyAddress,
        record.address,
    ].filter(Boolean)

    for (const source of sources) {
        const lat = Number(source.lat ?? source.latitude ?? source.Latitude)
        const lng = Number(source.lng ?? source.longitude ?? source.Longitude)
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng }
        }
    }
    return null
}

export const isValidMapLocation = (location) => (
    Boolean(location)
    && Number.isFinite(location.lat)
    && Number.isFinite(location.lng)
)

const normalizeFamilyName = (value) => normalize(value)
    .toLowerCase()
    .replace(/\bfamily\b/g, '')
    .replace(/[^a-z0-9]+/g, '')

export const getTeacherHomeMarkerId = (teacherKey) => `teacher-home-${teacherKey}`

export const findMarkerForSuggestion = (suggestion, markers = []) => {
    const teacherKey = getSuggestionTeacherKey(suggestion)
    const suggestionObject = getSuggestionObject(suggestion)
    const teacherMarkers = (Array.isArray(markers) ? markers : []).filter(
        (marker) => marker.teacherKey === teacherKey
    )
    const isCalendarStop = suggestionObject.type === 'calendarEvent'
        || Boolean(suggestionObject.familyId || suggestionObject.familyName)

    if (isCalendarStop) {
        const familyId = normalize(suggestionObject.familyId)
        if (familyId) {
            const matchingFamilyId = teacherMarkers.find(
                (marker) => marker.type === 'student-stop'
                    && normalize(marker.familyId) === familyId
            )
            if (matchingFamilyId) return matchingFamilyId
        }

        const familyName = normalizeFamilyName(suggestionObject.familyName)
        if (familyName) {
            const matchingFamilyName = teacherMarkers.find(
                (marker) => marker.type === 'student-stop'
                    && normalizeFamilyName(marker.familyName) === familyName
            )
            if (matchingFamilyName) return matchingFamilyName
        }

        return null
    }

    return teacherMarkers.find((marker) => marker.type === 'teacher-home') || null
}

export const getTeacherAvailabilityForSuggestion = (suggestion, teachers = []) => {
    const suggestionObject = getSuggestionObject(suggestion)
    const teacherId = getSuggestionTeacherId(suggestion)
    const teacherProfile = (Array.isArray(teachers) ? teachers : []).find(
        (teacher) => normalize(teacher?.TeacherID) === teacherId
    )

    return teacherProfile?.AvailabilityDetails
        || teacherProfile?.Availability
        || suggestionObject?.AvailabilityDetails
        || suggestionObject?.availabilityDetails
        || suggestionObject?.Availability
        || suggestionObject?.availability
        || null
}

export const availabilityRecordsOverlap = (familyAvailability, teacherAvailability) => {
    const family = normalizeOptimizerAvailability(familyAvailability)
    const teacher = normalizeOptimizerAvailability(teacherAvailability)
    if (!family.restricted || !teacher.restricted) return false

    return Array.from({ length: 7 }, (_, dayIndex) => dayIndex).some((dayIndex) => {
        const familyBlocks = family.byDay?.[dayIndex] || []
        const teacherBlocks = teacher.byDay?.[dayIndex] || []
        return familyBlocks.some((familyBlock) =>
            teacherBlocks.some((teacherBlock) =>
                Math.min(familyBlock.end, teacherBlock.end)
                    > Math.max(familyBlock.start, teacherBlock.start)
            )
        )
    })
}

export const getAvailabilityMatchingTeacherKeys = (
    suggestions,
    teachers,
    familyAvailability
) => {
    const matchingTeacherKeys = new Set()
    ;(Array.isArray(suggestions) ? suggestions : []).forEach((suggestion) => {
        const teacherKey = getSuggestionTeacherKey(suggestion)
        if (matchingTeacherKeys.has(teacherKey)) return
        const teacherAvailability = getTeacherAvailabilityForSuggestion(suggestion, teachers)
        if (availabilityRecordsOverlap(familyAvailability, teacherAvailability)) {
            matchingTeacherKeys.add(teacherKey)
        }
    })
    return matchingTeacherKeys
}
